import type { ComponentProps } from "react"
import type { Row } from "@tanstack/react-table"
import { useTableContext } from "./table.context"

export type TableSelectionCheckboxProps = ComponentProps<"input"> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: Row<any>
}

/**
 * Row selection checkbox with shift+click range selection support.
 * Tracks the last clicked row and selects the range between it and the current row on shift+click.
 */
function TableSelectionCheckbox(props: TableSelectionCheckboxProps) {
  const { row, onClick, ...inputProps } = props
  const { table, lastSelectedRowIdRef } = useTableContext()

  return (
    <input
      type="checkbox"
      aria-label="Select row"
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onClick={(e) => {
        e.stopPropagation()

        if (e.shiftKey && lastSelectedRowIdRef.current !== null) {
          const rows = table.getRowModel().rows
          const lastIdx = rows.findIndex(
            (r) => r.id === lastSelectedRowIdRef.current,
          )
          const currentIdx = rows.findIndex((r) => r.id === row.id)

          if (lastIdx !== -1 && currentIdx !== -1) {
            const start = Math.min(lastIdx, currentIdx)
            const end = Math.max(lastIdx, currentIdx)
            const isSelecting = !row.getIsSelected()

            for (let i = start; i <= end; i++) {
              rows[i].toggleSelected(isSelecting)
            }

            lastSelectedRowIdRef.current = row.id
            return
          }
        }

        row.toggleSelected()
        lastSelectedRowIdRef.current = row.id
        onClick?.(e)
      }}
      // Read-only since we handle selection in onClick
      onChange={() => {}}
      {...inputProps}
    />
  )
}

export default TableSelectionCheckbox
